import useCompanyGroupDetails from '@/modules/companyGroup/hooks/useCompanyGroupDetails';
import useCompany from '@/shared/hooks/useCompany/useCompany';
import { chunk, cloneDeep, uniq } from 'lodash';
import { useCallback, useMemo, useState } from 'react';

export const useCompaniesToExportBeneficiaries = () => {
  const maxCompaniesToSelect = 200;
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<string[]>([]);
  const { company } = useCompany();

  const { companyGroup, loading: loadingCompanyGroup } = useCompanyGroupDetails(
    company?.companyGroupId,
  );

  const allCompanyIds = useMemo(() => {
    return companyGroup?.companies.map((company) => company.id) || [];
  }, [companyGroup?.companies]);

  const sortedCompanies = useMemo(() => {
    return companyGroup?.companies
      ? [...companyGroup.companies].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
      : [];
  }, [companyGroup?.companies]);

  const paginatedCompanies = useMemo(() => {
    return chunk(sortedCompanies, 10);
  }, [sortedCompanies]);

  const countSelectedCompaniesMessage = useMemo(() => {
    const maxCountCompanies = Math.min(maxCompaniesToSelect, allCompanyIds.length);

    if (selectedCompanyIds.length === 0) {
      return `Nenhuma empresa selecionada de ${maxCountCompanies}`;
    }
    if (selectedCompanyIds.length === 1) {
      return `1 empresa selecionada de ${maxCountCompanies}`;
    }
    return `${selectedCompanyIds.length} empresas selecionadas de ${maxCountCompanies}`;
  }, [allCompanyIds.length, selectedCompanyIds.length]);

  const updateCompanyId = useCallback((selected: boolean, companyId: string) => {
    setSelectedCompanyIds((current) => {
      const clonedCurrent: string[] = uniq(cloneDeep(current));

      if (selected) {
        return [...clonedCurrent, companyId];
      }

      const companyIdIndex = clonedCurrent.findIndex(
        (currentCompanyId) => currentCompanyId === companyId,
      );

      if (companyIdIndex > -1) {
        clonedCurrent.splice(companyIdIndex, 1);
      }

      return clonedCurrent;
    });
  }, []);

  return {
    loadingCompanyGroup,
    allCompanyIds,
    selectedCompanyIds,
    maxCompaniesToSelect,
    updateCompanyId,
    paginatedCompanies,
    countSelectedCompaniesMessage,
    setSelectedCompanyIds,
  };
};
